import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { api, resolveImage } from "@/lib/api";
import { ArrowLeft, BedDouble, Bath, Maximize2, Calendar, MapPin, Play, X, ChevronLeft, ChevronRight, Expand } from "lucide-react";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import ShowingRequestModal from "@/components/ShowingRequestModal";
import { Reveal } from "@/hooks/useReveal";

// Extract a YouTube video ID from any common URL format.
const extractYouTubeId = (url) => {
  if (!url) return "";
  try {
    const u = new URL(url.trim());
    const host = u.hostname.replace(/^www\./, "");
    if (host === "youtu.be") {
      return u.pathname.replace(/^\//, "").split("/")[0] || "";
    }
    if (host.endsWith("youtube.com") || host.endsWith("youtube-nocookie.com")) {
      // /watch?v=ID
      const v = u.searchParams.get("v");
      if (v) return v;
      // /embed/ID  or  /shorts/ID  or  /live/ID
      const m = u.pathname.match(/^\/(?:embed|shorts|live|v)\/([A-Za-z0-9_-]+)/);
      if (m) return m[1];
    }
  } catch {
    // ignore parse errors
  }
  return "";
};

const Stat = ({ Icon, label, value }) => (
  <div className="flex items-start gap-4">
    <Icon size={20} className="text-black/40 mt-1 shrink-0" strokeWidth={1.25} />
    <div>
      <p className="text-[10px] tracking-[0.28em] uppercase text-black/40">{label}</p>
      <p className="text-lg mt-1">{value || "—"}</p>
    </div>
  </div>
);

// Full-screen lightbox / gallery
const Lightbox = ({ images, index, onClose, onIndex }) => {
  const total = images.length;
  const goPrev = useCallback(() => onIndex((index - 1 + total) % total), [index, total, onIndex]);
  const goNext = useCallback(() => onIndex((index + 1) % total), [index, total, onIndex]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") goPrev();
      else if (e.key === "ArrowRight") goNext();
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose, goPrev, goNext]);

  // Touch swipe support
  const [touchStart, setTouchStart] = useState(null);
  const onTouchStart = (e) => setTouchStart(e.touches[0].clientX);
  const onTouchEnd = (e) => {
    if (touchStart == null) return;
    const delta = e.changedTouches[0].clientX - touchStart;
    if (Math.abs(delta) > 50) (delta < 0 ? goNext : goPrev)();
    setTouchStart(null);
  };

  if (!images.length) return null;

  return (
    <div
      data-testid="listing-lightbox"
      onClick={onClose}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      className="fixed inset-0 z-[200] bg-black/95 backdrop-blur-sm flex items-center justify-center animate-in fade-in"
      role="dialog"
      aria-modal="true"
      aria-label="Photo gallery"
    >
      {/* Close */}
      <button
        data-testid="lightbox-close"
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        aria-label="Close gallery"
        className="absolute top-5 right-5 md:top-8 md:right-8 z-20 text-white/80 hover:text-white transition-colors"
      >
        <X size={28} strokeWidth={1.5} />
      </button>

      {/* Counter */}
      <span
        data-testid="lightbox-counter"
        className="absolute top-5 left-5 md:top-8 md:left-12 z-20 text-white/80 text-[11px] tracking-[0.32em] uppercase"
      >
        {index + 1} / {total}
      </span>

      {/* Prev */}
      {total > 1 && (
        <button
          data-testid="lightbox-prev"
          onClick={(e) => { e.stopPropagation(); goPrev(); }}
          aria-label="Previous photo"
          className="absolute left-3 md:left-8 z-20 w-12 h-12 md:w-14 md:h-14 rounded-full border border-white/30 text-white/80 hover:text-white hover:border-white/60 backdrop-blur-sm bg-black/20 flex items-center justify-center transition-all"
        >
          <ChevronLeft size={22} strokeWidth={1.5} />
        </button>
      )}

      {/* Image */}
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full h-full md:w-[90%] md:h-[90%] flex items-center justify-center px-14 md:px-0"
      >
        <img
          data-testid="lightbox-image"
          src={resolveImage(images[index])}
          alt={`Photo ${index + 1} of ${total}`}
          className="max-w-full max-h-full object-contain select-none"
          draggable={false}
        />
      </div>

      {/* Next */}
      {total > 1 && (
        <button
          data-testid="lightbox-next"
          onClick={(e) => { e.stopPropagation(); goNext(); }}
          aria-label="Next photo"
          className="absolute right-3 md:right-8 z-20 w-12 h-12 md:w-14 md:h-14 rounded-full border border-white/30 text-white/80 hover:text-white hover:border-white/60 backdrop-blur-sm bg-black/20 flex items-center justify-center transition-all"
        >
          <ChevronRight size={22} strokeWidth={1.5} />
        </button>
      )}

      {/* Hint */}
      <div className="absolute bottom-5 left-0 right-0 text-center text-white/40 text-[10px] tracking-[0.32em] uppercase pointer-events-none">
        Esc to close · ← → to navigate
      </div>
    </div>
  );
};

const ListingDetail = () => {
  const { slug } = useParams();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImg, setActiveImg] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [showingModalOpen, setShowingModalOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/listings/${slug}`);
        setListing(data);
      } catch {
        setListing(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  if (loading) {
    return (
      <main className="bg-white min-h-screen">
        <Navigation alwaysDark={true} />
        <div className="pt-40 text-center text-sm text-black/40">Loading...</div>
      </main>
    );
  }

  if (!listing) {
    return (
      <main className="bg-white min-h-screen">
        <Navigation alwaysDark={true} />
        <div className="pt-40 max-w-xl mx-auto px-6 text-center">
          <p className="overline mb-4">404</p>
          <h1 className="font-heading text-4xl mb-6">Listing not found</h1>
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm uppercase tracking-[0.24em] underline underline-offset-4"
          >
            <ArrowLeft size={14} /> Back to home
          </Link>
        </div>
      </main>
    );
  }

  const images = listing.image_urls?.length ? listing.image_urls : [];
  const youtubeId = extractYouTubeId(listing.youtube_url);

  return (
    <main data-testid="listing-detail-page" className="bg-white text-black">
      <Navigation alwaysDark={true} />

      {/* Hero image */}
      <section className="relative pt-24 md:pt-28">
        {images.length > 0 ? (
          <button
            type="button"
            onClick={() => setLightboxOpen(true)}
            data-testid="hero-image-open-lightbox"
            className="relative w-full block group cursor-zoom-in focus:outline-none"
            aria-label="Open photo gallery"
          >
            <img
              src={resolveImage(images[activeImg])}
              alt={listing.address}
              className="w-full aspect-[16/9] md:aspect-[21/9] object-cover bg-gray-100"
              data-testid="hero-image"
            />
            {/* Status tag */}
            <span className="absolute top-6 left-6 md:top-10 md:left-12 px-3 py-1 bg-white text-black text-[10px] tracking-[0.28em] uppercase">
              {listing.status}
            </span>
            {/* Expand hint */}
            <span className="absolute top-6 right-6 md:top-10 md:right-12 px-3 py-1.5 bg-black/60 text-white text-[10px] tracking-[0.28em] uppercase flex items-center gap-2 opacity-80 group-hover:opacity-100 transition-opacity backdrop-blur-sm">
              <Expand size={12} strokeWidth={1.5} />
              View all {images.length}
            </span>
          </button>
        ) : (
          <div className="w-full aspect-[21/9] bg-gray-100 flex items-center justify-center">
            <p className="text-[11px] uppercase tracking-[0.32em] text-black/30">No photos</p>
          </div>
        )}

        {/* Thumbnail strip */}
        {images.length > 1 && (
          <div className="max-w-[1400px] mx-auto px-6 md:px-12 mt-4 flex gap-3 overflow-x-auto pb-2">
            {images.map((u, i) => (
              <button
                key={i}
                onClick={() => { setActiveImg(i); setLightboxOpen(true); }}
                data-testid={`thumb-${i}`}
                aria-label={`Open photo ${i + 1}`}
                className={`shrink-0 w-24 h-16 md:w-32 md:h-20 overflow-hidden transition-opacity cursor-zoom-in ${
                  i === activeImg ? "opacity-100 ring-2 ring-black" : "opacity-60 hover:opacity-100"
                }`}
              >
                <img src={resolveImage(u)} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        )}
      </section>

      {/* Headline */}
      <section className="px-6 md:px-12 pt-20 md:pt-28">
        <div className="max-w-[1400px] mx-auto">
          <Reveal>
            <Link
              to="/#listings"
              className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] text-black/60 hover:text-black mb-8"
            >
              <ArrowLeft size={14} /> All Listings
            </Link>
          </Reveal>
          <Reveal delay={100}>
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-end">
              <div className="lg:col-span-8">
                <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl tracking-tight leading-[1.02]">
                  {listing.address}
                </h1>
                <p className="text-base md:text-lg text-black/60 mt-6 flex items-center gap-3">
                  <MapPin size={18} className="text-black/40" />
                  {[listing.city, listing.state, listing.zip_code].filter(Boolean).join(", ")}
                </p>
              </div>
              <div className="lg:col-span-4 lg:text-right">
                <p className="overline mb-2">List Price</p>
                <p className="font-heading text-4xl md:text-5xl tracking-tight">
                  {listing.price || "Inquire"}
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* YouTube video tour */}
      {youtubeId && (
        <section
          data-testid="listing-video-section"
          className="px-6 md:px-12 pt-20 md:pt-28"
        >
          <div className="max-w-[1400px] mx-auto">
            <Reveal>
              <div className="flex items-center gap-3 mb-8">
                <Play size={16} className="text-black/40" strokeWidth={1.5} />
                <p className="overline">Video Tour</p>
              </div>
            </Reveal>
            <Reveal delay={120}>
              <div className="relative w-full aspect-video bg-black overflow-hidden">
                <iframe
                  data-testid="listing-video-iframe"
                  src={`https://www.youtube-nocookie.com/embed/${youtubeId}?rel=0&modestbranding=1`}
                  title={`${listing.address} — Video Tour`}
                  loading="lazy"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  className="absolute inset-0 w-full h-full"
                />
              </div>
            </Reveal>
          </div>
        </section>
      )}

      {/* Stats + description */}
      <section className="px-6 md:px-12 pt-20 md:pt-28 pb-28">
        <div className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-7 order-2 lg:order-1">
            <Reveal>
              <p className="overline mb-6">About the property</p>
            </Reveal>
            <Reveal delay={120}>
              <div className="silver-rule mb-10" />
            </Reveal>
            <Reveal delay={200}>
              {listing.description ? (
                <div className="prose prose-lg max-w-none text-black/80 leading-relaxed whitespace-pre-line">
                  {listing.description}
                </div>
              ) : (
                <p className="text-black/50">
                  Full property narrative coming soon. Please contact Manzano for a
                  private tour and complete dossier.
                </p>
              )}
            </Reveal>
          </div>
          <aside className="lg:col-span-5 order-1 lg:order-2">
            <div className="bg-[#f6f6f6] p-8 md:p-12 space-y-8 sticky top-28">
              <Stat Icon={BedDouble} label="Bedrooms" value={listing.beds} />
              <Stat Icon={Bath} label="Bathrooms" value={listing.baths} />
              <Stat Icon={Maximize2} label="Square Feet" value={listing.sqft} />
              {listing.lot_size && (
                <Stat Icon={Maximize2} label="Lot Size" value={listing.lot_size} />
              )}
              {listing.year_built && (
                <Stat Icon={Calendar} label="Year Built" value={listing.year_built} />
              )}
              {listing.property_type && (
                <div>
                  <p className="text-[10px] tracking-[0.28em] uppercase text-black/40">Type</p>
                  <p className="text-lg mt-1">{listing.property_type}</p>
                </div>
              )}
              {listing.mls_number && (
                <div>
                  <p className="text-[10px] tracking-[0.28em] uppercase text-black/40">MLS #</p>
                  <p className="text-lg mt-1">{listing.mls_number}</p>
                </div>
              )}
              <div className="pt-4 border-t border-black/10">
                <button
                  type="button"
                  onClick={() => setShowingModalOpen(true)}
                  data-testid="cta-inquire"
                  className="block w-full text-center px-10 py-4 bg-black text-white border border-black text-[11px] uppercase tracking-[0.28em] hover:bg-white hover:text-black transition-all duration-500"
                >
                  Request a Private Showing
                </button>
              </div>
            </div>
          </aside>
        </div>
      </section>

      <Footer />

      {lightboxOpen && images.length > 0 && (
        <Lightbox
          images={images}
          index={activeImg}
          onIndex={setActiveImg}
          onClose={() => setLightboxOpen(false)}
        />
      )}

      {showingModalOpen && (
        <ShowingRequestModal
          listingSlug={slug}
          listingAddress={listing.address}
          onClose={() => setShowingModalOpen(false)}
        />
      )}
    </main>
  );
};

export default ListingDetail;
